import { FlexCol, FlexRow } from '@/components/common/Flex';
import { transparentize } from '@/util/chakra';
import {
    Box,
    CenterProps,
    FlexboxProps,
    forwardRef,
    HTMLChakraProps,
    useRadio,
    UseRadioProps,
    useTheme,
} from '@chakra-ui/react';
import React from 'react';
import FadeInBox from './FadeInBox';
import { Paragraph } from './Typography';

export type RadioInputSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

export interface RadioInputProps
    extends UseRadioProps,
        Omit<CenterProps, 'onChange'> {
    size?: RadioInputSize;
    header?: JSX.Element;
}

const RadioInput = forwardRef<RadioInputProps, 'input'>(
    (
        {
            id,
            name,
            value,
            isChecked,
            defaultChecked,
            isDisabled,
            isFocusable,
            isReadOnly,
            isInvalid,
            isRequired,
            onChange,
            children,
            autoFocus,
            header,
            size = 'md',
            ...rest
        },
        ref
    ) => {
        const { getInputProps, getCheckboxProps } = useRadio({
            id,
            name,
            value,
            isChecked,
            defaultChecked,
            isDisabled,
            isFocusable,
            isReadOnly,
            isInvalid,
            isRequired,
            onChange,
        });
        const theme = useTheme();
        const input = getInputProps();
        const checkbox = getCheckboxProps();

        const { w, h, px, py, fontSize } = {
            xs: { w: '16px', h: '16px', px: 1.5, py: 1.5, fontSize: 'sm' },
            sm: { w: '20px', h: '20px', px: 2, py: 2, fontSize: 'md' },
            md: { w: '20px', h: '20px', px: 3, py: 3, fontSize: 'md' },
            lg: { w: '24px', h: '24px', px: 4, py: 7, fontSize: 'lg' },
            xl: { w: '28px', h: '28px', px: 4, py: 10, fontSize: 'xl' },
        }[size];

        const checkboxEl = (
            <FadeInBox
                flex="none"
                w={w}
                h={h}
                mr={4}
                bg="white"
                border="1.5px solid transparent"
                borderColor="gray.300"
                borderRadius="50%"
                p="0.5%"
                initial={{ opacity: 1 }}
                animate={{
                    opacity: 1,
                    transform: isChecked
                        ? ['scale(1.2)', 'scale(1)']
                        : 'scale(1)',
                }}
            >
                {isChecked && (
                    <Box
                        w="100%"
                        h="100%"
                        bg="blue.400"
                        borderRadius="inherit"
                    />
                )}
            </FadeInBox>
        );

        const content = header ? (
            <FlexCol h="full" w="full">
                <FlexRow align="center">
                    {checkboxEl}
                    {header}
                </FlexRow>
                <input {...input} ref={ref} autoFocus={autoFocus} />
                {children}
            </FlexCol>
        ) : (
            <>
                {checkboxEl}
                <input {...input} ref={ref} autoFocus={autoFocus} />
                {children}
            </>
        );

        return (
            <FlexRow
                as="label"
                {...checkbox}
                alignItems="center"
                cursor="pointer"
                border="1px solid transparent"
                borderColor="border"
                borderRadius="6px"
                boxShadow={isChecked ? 'sm' : undefined}
                px={px}
                py={py}
                position="relative"
                transition="all 150ms ease-out"
                overflow="hidden"
                _hover={{
                    borderColor: 'blue.400',
                }}
                _checked={{
                    background: transparentize('blue.400', 0.05)(theme),
                    borderColor: 'blue.400',
                }}
                fontSize={fontSize}
                {...rest}
            >
                {content}
            </FlexRow>
        );
    }
);

export default RadioInput;

type RadioInputLabelProps = FlexboxProps &
    HTMLChakraProps<'div'> & {
        label: string;
        children?: React.ReactNode;
    };

export const RadioInputLabel: React.FC<RadioInputLabelProps> = ({
    label,
    children,
    ...rest
}) => {
    return (
        <FlexCol alignItems="flex-start" {...rest}>
            <Paragraph fontWeight="medium" position="relative">
                <Box
                    as="span"
                    position="absolute"
                    top={0}
                    bottom={0}
                    left={0}
                    right={0}
                    bg="white"
                    zIndex={-1}
                />
                {label}
            </Paragraph>
            {children}
        </FlexCol>
    );
};
